import { create } from 'zustand'
import type { Locale } from '@/lib/i18n'
import type { KioskRemoteConfig } from '@/lib/kiosk-config'
import type {
  AddonDefinition,
  AltGroupMetaMap,
  AltGroupV2Definition,
  AvailableAddonProduct,
  CartItem,
  CompletedOrder,
  DeliveryLocation,
  KioskConfig,
  KioskStep,
  MenuPage,
  MenuType,
  PaymentMethod,
  Product,
  ResolvedAddonItem,
  SelectedAlternative,
  TimeSlot,
} from '@/lib/types'

interface KioskState {
  // Config
  config: KioskConfig | null
  remoteConfig: KioskRemoteConfig | null
  parseToken: string | null
  tillName: string

  // Navigation
  step: KioskStep
  menuType: MenuType | null
  locale: Locale

  // Menu
  pages: MenuPage[]
  restaurantName: string
  paymentMethods: PaymentMethod[]
  deliveryLocations: DeliveryLocation[]
  timeSlots: TimeSlot[]
  showsTip: boolean
  showsStripeSwish: boolean
  /** Native Till.showsMenuType — controls which menu types the kiosk offers.
   *  'All' shows the EatIn/TakeAway choice screen; 'EatIn' or 'TakeAway'
   *  alone auto-advances past the choice. */
  showsMenuType: 'All' | 'EatIn' | 'TakeAway'
  queueTime: string | null
  posTerminalId: string | null
  /** Which provider the selected posTerminalId belongs to (e.g. 'Stripe', 'NetsConnectCloud') */
  posTerminalProvider: string | null
  /** Full list of this till's posTerminals as reported by LocoPOS — used for diagnostics */
  posTerminals: Array<{
    id: string
    name: string
    isOnline: boolean
    isActive: boolean
    paymentProvider: string
  }>
  receiptPrinterId: string | null
  sendOrderToKitchen: string | null
  /** Last payment-intent response from LocoPOS — used in admin modal diagnostics */
  lastPaymentDebug: string | null

  // Browsing
  selectedCategory: string | null
  selectedProduct: Product | null

  // Addons (upsell efter Lägg i varukorg)
  availableAddons: AddonDefinition[]
  // Metadata för native alternativgrupper (layout etc.) — { [altGroupId]: { layout, internalName } }
  altGroupMeta: AltGroupMetaMap
  // V.2 alternativgrupp-bibliotek (isolerat från native) — laddas från pong.altGroupLibrary
  altGroupV2Library: AltGroupV2Definition[]
  // Master-switch: om false faller kiosken tillbaka till native för alla produkter
  altGroupV2MasterEnabled: boolean
  // Alla MenuAddonProducts i restaurangen \u2014 anv\u00e4nds for att rendera V.2-items
  // (bild, namn, pris) och skicka addon-id + stateId i OrderAlternative.
  availableAddonProducts: AvailableAddonProduct[]
  addonFlow: {
    steps: AddonDefinition[] // ordnade steg för pågående flöde
    currentIdx: number // vilket steg visas just nu
    selected: ResolvedAddonItem[] // items tillagda så här långt
  } | null
  activePage: number
  currentCategoryLabel: string | null // shown in landscape header when inside a category
  backAction: (() => void) | null // what landscape header's "Tillbaka" should do
  /** Vad som händer EFTER att en produkt lagts till i kundvagnen (post-add-flow).
   *  Skiljs medvetet från backAction så vi kan ha olika beteenden — t.ex. när
   *  skipCategoryGrid är på och user är i HELA MENYN ska "tillbaka"-knappen
   *  gå till menuTypeSelect, men add-to-cart ska bara stänga modalen och
   *  låta user fortsätta beställa. ProductGrid sätter denna baserat på
   *  current viewingPage. Om null faller ProductModal tillbaka på backAction. */
  afterProductAddedAction: (() => void) | null

  // Cart
  cart: CartItem[]
  tipPercent: number
  selectedDeliveryLoc: string
  selectedTimeSlot: string | null
  orderMessage: string

  // Payment
  payMethod: 'card' | 'swish'
  swishQr: string | null
  processing: boolean
  error: string | null

  // Order result
  completedOrder: CompletedOrder | null
  receiptHtml: string

  // Loading
  loading: boolean

  // Actions
  setConfig: (config: KioskConfig) => void
  setRemoteConfig: (config: KioskRemoteConfig | null) => void
  setTillName: (name: string) => void
  setParseToken: (token: string | null) => void
  setStep: (step: KioskStep) => void
  setMenuType: (type: MenuType) => void
  setLocale: (locale: Locale) => void
  setPages: (pages: MenuPage[]) => void
  setRestaurantName: (name: string) => void
  setPaymentMethods: (methods: PaymentMethod[]) => void
  setDeliveryLocations: (locations: DeliveryLocation[]) => void
  setTimeSlots: (slots: TimeSlot[]) => void
  setShowsTip: (show: boolean) => void
  setShowsStripeSwish: (show: boolean) => void
  setShowsMenuType: (value: 'All' | 'EatIn' | 'TakeAway') => void
  setPosTerminalId: (id: string | null) => void
  setPosTerminalProvider: (provider: string | null) => void
  setPosTerminals: (
    terminals: Array<{
      id: string
      name: string
      isOnline: boolean
      isActive: boolean
      paymentProvider: string
    }>,
  ) => void
  setReceiptPrinterId: (id: string | null) => void
  setSendOrderToKitchen: (v: string | null) => void
  setLastPaymentDebug: (v: string | null) => void
  setQueueTime: (time: string | null) => void
  setSelectedCategory: (cat: string | null) => void
  setSelectedProduct: (product: Product | null) => void
  setActivePage: (page: number) => void
  setCurrentCategoryLabel: (label: string | null) => void
  setBackAction: (action: (() => void) | null) => void
  setAfterProductAddedAction: (action: (() => void) | null) => void
  setTipPercent: (pct: number) => void
  setSelectedDeliveryLoc: (loc: string) => void
  setSelectedTimeSlot: (slot: string | null) => void
  setOrderMessage: (msg: string) => void
  setPayMethod: (method: 'card' | 'swish') => void
  setSwishQr: (qr: string | null) => void
  setProcessing: (p: boolean) => void
  setError: (err: string | null) => void
  setCompletedOrder: (order: CompletedOrder | null) => void
  setReceiptHtml: (html: string) => void
  setLoading: (l: boolean) => void

  /**
   * Lägger produkt i kundvagnen med alternatives. V.2-val läggs som native-
   * alternatives tack vare osynliga shadow-MenuAlternativeGroups som backoffice
   * skapar automatiskt (V.2-grupp → shadow-native-grupp). Resultat: indent-
   * format på kvitto/bong + betalning fungerar.
   */
  addToCart: (product: Product, alternatives: SelectedAlternative[], message?: string) => void
  updateCartCount: (idx: number, delta: number) => void
  clearCart: () => void

  setAvailableAddons: (addons: AddonDefinition[]) => void
  setAltGroupMeta: (meta: AltGroupMetaMap) => void
  setAltGroupV2Library: (lib: AltGroupV2Definition[]) => void
  setAltGroupV2MasterEnabled: (enabled: boolean) => void
  setAvailableAddonProducts: (addons: AvailableAddonProduct[]) => void
  /** Starta addon-flöde för nyss tillagd produkt. Returnerar true om flödet visas. */
  beginAddonFlow: (product: Product, menuType: MenuType) => boolean
  /** Lägg till items till pågående flödes selected-lista (används av AddonStepModal). */
  addToAddonFlowSelection: (items: ResolvedAddonItem[]) => void
  /** Nästa steg eller commit + stäng om sista. */
  advanceAddonFlow: () => void
  /** Avbryt flödet utan att committa. */
  cancelAddonFlow: () => void

  totalAmount: () => number
  totalItems: () => number

  resetToAttractor: () => void
  resetForNewOrder: () => void
}

export const useKioskStore = create<KioskState>((set, get) => ({
  // Initial state
  config: null,
  remoteConfig: null,
  parseToken: null,
  tillName: '',
  step: 'menuTypeSelect',
  menuType: null,
  locale: 'sv',
  pages: [],
  restaurantName: '',
  paymentMethods: [],
  deliveryLocations: [],
  timeSlots: [],
  showsTip: false,
  showsStripeSwish: false,
  showsMenuType: 'All',
  posTerminalId: null,
  posTerminalProvider: null,
  posTerminals: [],
  receiptPrinterId: null,
  sendOrderToKitchen: null,
  lastPaymentDebug: null,
  queueTime: null,
  selectedCategory: null,
  selectedProduct: null,
  availableAddons: [],
  altGroupMeta: {},
  altGroupV2Library: [],
  // Master-switch default TRUE \u2014 V.2 anv\u00e4nds d\u00e4r det \u00e4r kopplat.
  // AV = n\u00f6dstopp, alla V.2-kopplade produkter faller tillbaka till native.
  altGroupV2MasterEnabled: true,
  availableAddonProducts: [],
  addonFlow: null,
  activePage: 0,
  currentCategoryLabel: null,
  backAction: null,
  afterProductAddedAction: null,
  cart: [],
  tipPercent: 0,
  selectedDeliveryLoc: '',
  selectedTimeSlot: null,
  orderMessage: '',
  payMethod: 'card',
  swishQr: null,
  processing: false,
  error: null,
  completedOrder: null,
  receiptHtml: '',
  loading: false,

  // Setters
  setConfig: (config) => set({ config }),
  setRemoteConfig: (remoteConfig) => set({ remoteConfig }),
  setTillName: (tillName) => set({ tillName }),
  setParseToken: (parseToken) => set({ parseToken }),
  setStep: (step) => set({ step, error: null }),
  setMenuType: (menuType) => set({ menuType }),
  setLocale: (locale) => set({ locale }),
  setPages: (pages) => set({ pages }),
  setRestaurantName: (restaurantName) => set({ restaurantName }),
  setPaymentMethods: (paymentMethods) => set({ paymentMethods }),
  setDeliveryLocations: (deliveryLocations) => set({ deliveryLocations }),
  setTimeSlots: (timeSlots) => set({ timeSlots }),
  setShowsTip: (showsTip) => set({ showsTip }),
  setShowsStripeSwish: (showsStripeSwish) => set({ showsStripeSwish }),
  setShowsMenuType: (showsMenuType) => set({ showsMenuType }),
  setPosTerminalId: (posTerminalId) => set({ posTerminalId }),
  setPosTerminalProvider: (posTerminalProvider) => set({ posTerminalProvider }),
  setPosTerminals: (posTerminals) => set({ posTerminals }),
  setReceiptPrinterId: (receiptPrinterId) => set({ receiptPrinterId }),
  setSendOrderToKitchen: (sendOrderToKitchen) => set({ sendOrderToKitchen }),
  setLastPaymentDebug: (lastPaymentDebug) => set({ lastPaymentDebug }),
  setQueueTime: (queueTime) => set({ queueTime }),
  setSelectedCategory: (selectedCategory) => set({ selectedCategory }),
  setSelectedProduct: (selectedProduct) => set({ selectedProduct }),
  setActivePage: (activePage) => set({ activePage }),
  setCurrentCategoryLabel: (currentCategoryLabel) => set({ currentCategoryLabel }),
  setBackAction: (backAction) => set({ backAction }),
  setAfterProductAddedAction: (action) => set({ afterProductAddedAction: action }),
  setTipPercent: (tipPercent) => set({ tipPercent }),
  setSelectedDeliveryLoc: (selectedDeliveryLoc) => set({ selectedDeliveryLoc }),
  setSelectedTimeSlot: (selectedTimeSlot) => set({ selectedTimeSlot }),
  setOrderMessage: (orderMessage) => set({ orderMessage }),
  setPayMethod: (payMethod) => set({ payMethod }),
  setSwishQr: (swishQr) => set({ swishQr }),
  setProcessing: (processing) => set({ processing }),
  setError: (error) => set({ error }),
  setCompletedOrder: (completedOrder) => set({ completedOrder }),
  setReceiptHtml: (receiptHtml) => set({ receiptHtml }),
  setLoading: (loading) => set({ loading }),

  // Cart actions
  addToCart: (product, alternatives, message) => {
    if (!product.properties) return
    const properties = product.properties
    const basePrice = properties.price || 0
    const addonTotal = alternatives.reduce((s, a) => s + (a.addonProduct?.productPrice || 0) * a.count, 0)
    // Allow only if we end up with a positive price (size-based products need alternatives)
    if (basePrice + addonTotal <= 0) return
    const altSummary = alternatives.map((a) => a.alternativeItemName + (a.count > 1 ? ` x${a.count}` : '')).join(', ')
    set((state) => ({
      cart: [
        ...state.cart,
        {
          productId: properties.id,
          stateId: properties.stateId,
          name: product.name,
          price: basePrice + addonTotal,
          count: 1,
          alternatives,
          alternativesSummary: altSummary,
          imageUrl: properties.imageUrl,
          message: message?.trim() || undefined,
        },
      ],
      selectedProduct: null,
    }))
  },

  updateCartCount: (idx, delta) => {
    set((state) => ({
      cart: state.cart.map((item, i) => (i === idx ? { ...item, count: Math.max(0, item.count + delta) } : item)).filter((i) => i.count > 0),
    }))
  },

  clearCart: () => set({ cart: [], tipPercent: 0, orderMessage: '' }),

  setAvailableAddons: (availableAddons) => set({ availableAddons }),
  setAltGroupMeta: (altGroupMeta) => set({ altGroupMeta }),
  setAltGroupV2Library: (altGroupV2Library) => set({ altGroupV2Library }),
  setAltGroupV2MasterEnabled: (altGroupV2MasterEnabled) => set({ altGroupV2MasterEnabled }),
  setAvailableAddonProducts: (availableAddonProducts) => set({ availableAddonProducts }),

  // Starta addon-flöde för nyss tillagd produkt.
  // Returnerar true om minst ett steg kommer visas.
  beginAddonFlow: (product, menuType) => {
    if (!product.addonRefs) return false
    let refsByType: Record<string, string[]> = {}
    try {
      const parsed = JSON.parse(product.addonRefs)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        // Nytt format: { EatIn: ['id1','id2'], TakeAway: [...], ... }
        refsByType = parsed as Record<string, string[]>
      } else if (Array.isArray(parsed)) {
        // Legacy-format (commit 7a19a09): [{ addonId, menuTypes: ['EatIn',...] }, ...]
        // Om menuTypes ar tom lista = "alla menytyper".
        const ALL = ['EatIn', 'TakeAway', 'Delivery', 'DeliveryHero', 'UberEats', 'Wolt']
        for (const r of parsed) {
          if (!r || typeof r !== 'object' || !r.addonId) continue
          const targets = Array.isArray(r.menuTypes) && r.menuTypes.length > 0 ? r.menuTypes : ALL
          for (const t of targets) {
            if (!refsByType[t]) refsByType[t] = []
            if (!refsByType[t].includes(r.addonId)) refsByType[t].push(String(r.addonId))
          }
        }
      }
    } catch {
      return false
    }
    const ids = refsByType[menuType] || []
    if (ids.length === 0) return false
    const lib = get().availableAddons
    const steps = ids.map((id) => lib.find((a) => a.id === id)).filter((a): a is AddonDefinition => !!a)
    if (steps.length === 0) return false
    set({ addonFlow: { steps, currentIdx: 0, selected: [] } })
    return true
  },

  addToAddonFlowSelection: (items) => {
    set((state) => {
      if (!state.addonFlow) return {}
      return {
        addonFlow: { ...state.addonFlow, selected: [...state.addonFlow.selected, ...items] },
      }
    })
  },

  advanceAddonFlow: () => {
    const flow = get().addonFlow
    if (!flow) return
    if (flow.currentIdx < flow.steps.length - 1) {
      // Nästa steg
      set({ addonFlow: { ...flow, currentIdx: flow.currentIdx + 1 } })
    } else {
      // Sista steget klart — committa alla selected till varukorgen som egna rader.
      set((state) => {
        const cartAdditions: CartItem[] = flow.selected.map((it) => ({
          productId: it.productId,
          stateId: it.productStateId,
          name: it.name,
          price: it.price, // override (vad kund betalar per enhet)
          menuPrice: it.menuPrice, // menyns pris (f\u00f6r discount-ber\u00e4kning)
          count: it.count,
          alternatives: [],
          alternativesSummary: '',
          imageUrl: it.imageUrl,
          isAddon: true,
        }))
        return {
          cart: [...state.cart, ...cartAdditions],
          addonFlow: null,
        }
      })
      // Efter sista addon-steget: kör post-add-flow så kund kommer tillbaka
      // till huvudkategorisidan istället för att hamna på samma produkt igen.
      // afterProductAddedAction är skild från backAction så vi kan ha "stay-here"-
      // beteende när skipCategoryGrid är på (HELA MENYN-vyn).
      const postAdd = get().afterProductAddedAction ?? get().backAction
      postAdd?.()
    }
  },

  cancelAddonFlow: () => {
    set({ addonFlow: null })
    // Efter avbrutet addon-flöde: använder samma post-add-flow.
    const postAdd = get().afterProductAddedAction ?? get().backAction
    postAdd?.()
  },

  totalAmount: () => get().cart.reduce((s, i) => s + i.price * i.count, 0),
  totalItems: () => get().cart.reduce((s, i) => s + i.count, 0),

  // Reset entire kiosk back to attractor (between customers)
  resetToAttractor: () =>
    set({
      step: 'menuTypeSelect',
      menuType: null,
      pages: [],
      selectedCategory: null,
      selectedProduct: null,
      activePage: 0,
      currentCategoryLabel: null,
      backAction: null,
      afterProductAddedAction: null,
      cart: [],
      tipPercent: 0,
      selectedDeliveryLoc: '',
      selectedTimeSlot: null,
      orderMessage: '',
      payMethod: 'card',
      swishQr: null,
      processing: false,
      error: null,
      completedOrder: null,
      receiptHtml: '',
      loading: false,
    }),

  // Reset for a new order (after receipt, keep menu loaded)
  resetForNewOrder: () =>
    set({
      step: 'browsing',
      selectedCategory: null,
      selectedProduct: null,
      currentCategoryLabel: null,
      backAction: null,
      afterProductAddedAction: null,
      cart: [],
      tipPercent: 0,
      orderMessage: '',
      payMethod: 'card',
      swishQr: null,
      processing: false,
      error: null,
      completedOrder: null,
      receiptHtml: '',
    }),
}))
